import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

export const defaultSettings: Record<string, string> = {
  hero_label: 'Пицария Густо',
  hero_title_1: 'Вкусът, който',
  hero_title_2: 'обединява',
  hero_subtitle: 'Изпечена със страст, доставена с грижа. Открий истинската италианска традиция във всяка хапка.',
  hero_image_url: '',
  hero_video_webm_url: '',
  hero_video_mp4_url: '',
  hero_btn_1: 'Разгледай Менюто',
  hero_btn_2: 'Поръчай сега',
  about_label: 'Нашата философия',
  about_title: 'Изкуството на\nбавната ферментация',
  about_text_1: 'Всяка велика пица започва с безкрайно търпение. Нашето тесто се приготвя от селектирани италиански брашна тип 00 и зрее точно 48 часа при контролирана температура.',
  about_text_2: 'Използваме само автентични продукти — сладки домати Сан Марцано от склоновете на Везувий, прясна моцарела Фиор ди Лате и зехтин екстра върджин. Печем на 450 градуса в пещ на дърва за точно 90 секунди до постигане на перфектната леопардова коричка.',
  about_stat_1_val: '48',
  about_stat_1_lbl: 'Ферментация',
  about_stat_2_val: '450',
  about_stat_2_lbl: 'Традиционна пещ',
  about_image: '',
  gallery_label: 'Ежедневие',
  gallery_title: 'Последвай ни в Instagram',
  gallery_ig_handle: '@gustopizzeria',
  contact_desc: 'Вкусът на истинската Италия, приготвен с безкомпромисно качество и любов към занаята.',
  contact_address: 'ул. Прогона 3, Кюстендил',
  contact_phone: '0700 50 605',
  contact_email: 'pizzariagusto123@gmail.com',
  contact_hours: 'Всеки ден от 10:00 до 00:00',
  menu_label: 'Нашето меню',
  menu_title: 'Открий любимите си вкусове',
  menu_categories: 'Салати,Супи,Предястия,Основни ястия,Паста и ризото,Пица,Бургери,Хляб,Риба и морски дарове,Скара,Десерти,Топли напитки,Безалкохолни напитки,Алкохолни напитки,Обедно меню',
  menu_subcategories: 'Наливна бира,Бутилирана бира,Ракия,Узо,Вино,Вино специална селекция,Наливно вино,Уиски,Водка',
  reviews_label: 'Отзиви',
  reviews_title: 'Какво казват клиентите ни',
  reviews_json: JSON.stringify([
    {
      name: 'Николай Георгиев',
      role: 'Local Guide',
      text: 'Най-добрата пица извън Италия, която съм ял. Тестото е изключително въздушно, не тежи на стомаха, а съставките си личи, че са първокачествени. Препоръчвам Маргаритата!',
      rating: 5
    },
    {
      name: 'Мария Стоянова',
      role: 'Редовен клиент',
      text: 'Доставката пристигна за по-малко от 40 минути в Гео Милев. Пицата беше още топла, а кутията - суха, без конденз. Перфектна бърза вечеря за семейството.',
      rating: 5
    },
    {
      name: 'Александър Димитров',
      role: 'Ценител',
      text: 'Вниманието към детайла е огромно. Прошутото крудо се слага след изпичане, точно както трябва да бъде. Обслужването по WhatsApp също е супер бързо и удобно.',
      rating: 5
    }
  ]),
  review_1_name: 'Николай Георгиев',
  review_1_role: 'Local Guide',
  review_1_text: 'Най-добрата пица извън Италия, която съм ял. Тестото е изключително въздушно, не тежи на стомаха, а съставките си личи, че са първокачествени. Препоръчвам Маргаритата!',
  review_2_name: 'Мария Стоянова',
  review_2_role: 'Редовен клиент',
  review_2_text: 'Доставката пристигна за по-малко от 40 минути в Гео Милев. Пицата беше още топла, а кутията - суха, без конденз. Перфектна бърза вечеря за семейството.',
  review_3_name: 'Александър Димитров',
  review_3_role: 'Ценител',
  review_3_text: 'Вниманието към детайла е огромно. Прошутото крудо се слага след изпичане, точно както трябва да бъде. Обслужването по WhatsApp също е супер бързо и удобно.',
  delivery_title: 'Бърза доставка до твоята врата',
  delivery_feat_1: 'Време на доставка: 35–50 мин',
  delivery_feat_2: 'Кюстендил и региона',
  delivery_feat_3: 'Безплатна доставка над 30 €',
  delivery_feat_4: 'Плащане: кеш или карта при доставка',
  delivery_feat_5: 'Поръчай по телефона: 0700 50 605',
  delivery_btn: 'Поръчай сега',
  catering_text: 'Предлагаме и <strong>професионален кетъринг</strong> за вашите събития.',
  carwash_text: 'Оставете колата в <strong>нашата автомивка</strong>, докато се наслаждавате на храната.'
};

interface SettingsContextType {
  settings: Record<string, string>;
  loading: boolean;
  refreshSettings: () => Promise<void>;
}

const SettingsContext = createContext<SettingsContextType>({ 
  settings: defaultSettings, 
  loading: true, 
  refreshSettings: async () => {} 
});

// Pre-fetch settings outside the React lifecycle to avoid waterfall chaining
let globalSettingsPromise: any = null;
if (typeof window !== 'undefined') {
  globalSettingsPromise = supabase.from('app_settings').select('*');
}

export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<Record<string, string>>(defaultSettings);
  const [loading, setLoading] = useState(true);

  async function fetchSettings(ignoreCache = false) {
    try {
      let cached = null;
      if (!ignoreCache) {
        try {
          cached = localStorage.getItem('settings_cache');
        } catch (e) {
          console.warn('localStorage not available', e);
        }
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            if (Object.keys(parsed).length > 0) {
              setSettings(parsed);
              setLoading(false);
            }
          } catch (e) {}
        }
      }

      let data = null;
      let error = null;

      if (globalSettingsPromise && !ignoreCache) {
        const response = await globalSettingsPromise;
        data = response.data;
        error = response.error;
        globalSettingsPromise = null;
      } else {
        const response = await supabase.from('app_settings').select('*');
        data = response.data;
        error = response.error;
      }

      if (!error && data) {
        const dict: Record<string, string> = { ...defaultSettings };
        data.forEach((item: any) => {
          if (item.value !== null && item.value !== undefined) {
            dict[item.key] = item.value;
          }
        });
        
        const currentCats = dict.menu_categories.split(',').map(s => s.trim()).filter(Boolean);
        const defaultCats = defaultSettings.menu_categories.split(',').map(s => s.trim()).filter(Boolean);
        
        let mergedCats = [...currentCats];
        for (const c of defaultCats) {
          if (!mergedCats.includes(c)) {
            mergedCats.push(c);
          }
        }
        
        if (dict.menu_categories !== mergedCats.join(',')) {
           dict.menu_categories = mergedCats.join(',');
        }

        setSettings(dict);
        try {
          localStorage.setItem('settings_cache', JSON.stringify(dict));
        } catch (e) {
          console.warn('localStorage setItem failed');
        }
      }
    } catch (err) {
      console.error('Failed to parse settings', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchSettings();
  }, []);

  const refreshSettings = async () => {
    await fetchSettings(true);
  };

  return <SettingsContext.Provider value={{ settings, loading, refreshSettings }}>{children}</SettingsContext.Provider>;
}

export function useSettings() {
  return useContext(SettingsContext);
}
